import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import api from '../../api/axios';

export default function ProductDetailPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Date Filter States
  const [dates, setDates] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    return [start, end];
  });

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      let url = `product/get-product/${id}`;

      if (dates && dates[0] && dates[1]) {
        const start = dates[0].toISOString();
        const end = dates[1].toISOString();
        url += `?startDate=${start}&endDate=${end}`;
      }

      const res = await api.get(url);
      setProduct(res.data.data);
    } catch (err) {
      toast.current.show({
        severity: 'error',
        summary: 'Hata',
        detail: 'Ürün detayları yüklenemedi.',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductDetail();
  }, [id]);

  const handleSearch = () => {
    fetchProductDetail();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <i className="pi pi-spin pi-spinner text-4xl text-blue-600"></i>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <Card>Ürün bulunamadı.</Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 animate-fade-in">
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

      {/* Üst Kısım: Ürün Bilgi Kartı */}
      <Card
        title={
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="!bg-blue-600 p-4 rounded-xl shadow-md">
                  <i className="pi pi-box text-white text-xl"></i>
                </div>
                <div>
                  <h1 className="text-xl font-black text-slate-900 uppercase">{product.name}</h1>
                  <p className="text-xs text-slate-500 font-bold">{product.brandName || 'Markasız'} | {product.barcode || 'Barkodsuz'}</p>
                </div>
              </div>
              <Button
                label="Geri"
                icon="pi pi-arrow-left"
                className="p-button-outlined p-button-secondary rounded-xl"
                onClick={() => navigate('/products')}
              />
            </div>

            {/* Filtre Alanı */}
            <div className="!bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col md:flex-row items-end gap-3">
              <div className="flex-1 w-full">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">TARİH ARALIĞI</label>
                <Calendar
                  value={dates}
                  onChange={(e) => setDates(e.value)}
                  selectionMode="range"
                  readOnlyInput
                  hideOnRangeSelection
                  showIcon
                  className="w-full"
                  inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                  placeholder="Tarih aralığı seçin..."
                  dateFormat="dd.mm.yy"
                  locale="tr"
                />
              </div>
              <Button
                label="ARA"
                icon="pi pi-search"
                className="!bg-slate-900 border-none px-8 py-2.5 rounded-xl font-black text-xs h-[42px] transition-all active:scale-95"
                onClick={handleSearch}
                loading={loading}
              />
            </div>
          </div>
        }
        className="shadow-lg border-none rounded-2xl"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">SON ALIŞ FİYATI</div>
            <div className="text-xl font-black text-slate-800">
              {product.lastPurchase?.unitPrice?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
            </div>
            <div className="text-[10px] text-slate-500 font-medium mt-1">
              {product.lastPurchase?.supplierName || 'Kayıt yok'}
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">TOPLAM ALIŞ MİKTARI</div>
            <div className="text-xl font-black text-blue-800">
              {product.totalPurchaseQuantity?.toLocaleString('tr-TR')} {product.unitType}
            </div>
          </div>

          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
            <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">TOPLAM SATIŞ MİKTARI</div>
            <div className="text-xl font-black text-emerald-800">
              {product.totalSaleQuantity?.toLocaleString('tr-TR')} {product.unitType}
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${product.currentStockQuantity <= product.criticalStockQuantity ? 'bg-red-50 border-red-100' : 'bg-slate-900 border-slate-800 text-white'}`}>
            <div className={`text-[10px] font-black uppercase tracking-widest mb-1 ${product.currentStockQuantity <= product.criticalStockQuantity ? 'text-red-400' : 'text-slate-400'}`}>MEVCUT STOK MİKTARI</div>
            <div className={`text-xl font-black ${product.currentStockQuantity <= product.criticalStockQuantity ? 'text-red-800' : 'text-white'}`}>
              {product.currentStockQuantity?.toLocaleString('tr-TR')} {product.unitType}
            </div>
            {product.currentStockQuantity <= product.criticalStockQuantity && (
              <div className="text-[10px] text-red-600 font-bold mt-1 uppercase">KRİTİK STOK SEVİYESİ!</div>
            )}
          </div>
        </div>
      </Card>

      {/* Alt Kısım: Hareketler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sol: Alış Bilgileri */}
        <div className="bg-white shadow-lg rounded-2xl overflow-hidden border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 w-1.5 h-5 rounded-full"></div>
              <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Alış Bilgileri</h2>
            </div>
            <Tag value={`${product.purchases?.length || 0} İŞLEM`} className="bg-blue-600 text-[10px] font-black" />
          </div>
          <DataTable
            value={product.purchases}
            rows={10}
            paginator
            responsiveLayout="scroll"
            className="p-datatable-sm"
            emptyMessage="Alış kaydı bulunamadı."
          >
            <Column field="supplierName" header="TEDARİKÇİ" sortable className="font-bold text-xs" />
            <Column
              field="quantity"
              header="MİKTAR"
              sortable
              body={(row) => (
                <span className="font-black text-slate-700">
                  {row.quantity} {row.unit}
                </span>
              )}
            />
            <Column
              field="unitPrice"
              header="ALIŞ FİYATI"
              sortable
              body={(row) => (
                <span className="font-black text-blue-600">
                  {row.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              )}
            />
            <Column
              field="purchaseDate"
              header="TARİH"
              sortable
              body={(row) => (
                <span className="text-slate-500 font-medium text-[11px]">
                  {new Date(row.purchaseDate).toLocaleDateString('tr-TR')}
                </span>
              )}
            />
          </DataTable>
        </div>

        {/* Sağ: Satış Bilgileri */}
        <div className="bg-white shadow-lg rounded-2xl overflow-hidden border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-100 bg-emerald-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-600 w-1.5 h-5 rounded-full"></div>
              <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Satış Bilgileri</h2>
            </div>
            <Tag value={`${product.sales?.length || 0} İŞLEM`} className="bg-emerald-600 text-[10px] font-black" />
          </div>
          <DataTable
            value={product.sales}
            rows={10}
            paginator
            responsiveLayout="scroll"
            className="p-datatable-sm"
            emptyMessage="Satış kaydı bulunamadı."
          >
            <Column
              field="saleDate"
              header="TARİH"
              sortable
              body={(row) => (
                <span className="text-slate-500 font-medium text-[11px]">
                  {new Date(row.saleDate).toLocaleDateString('tr-TR')}
                </span>
              )}
            />
            <Column field="customerName" header="MÜŞTERİ" sortable className="font-bold text-xs" />
            <Column
              field="quantity"
              header="ADET"
              sortable
              body={(row) => (
                <span className="font-black text-slate-700">{row.quantity}</span>
              )}
            />
            <Column
              field="unitPrice"
              header="BİRİM FİYAT"
              sortable
              body={(row) => (
                <span className="font-black text-slate-600">
                  {row.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              )}
            />
            <Column
              field="totalAmount"
              header="TOPLAM"
              sortable
              body={(row) => (
                <span className="font-black text-emerald-600">
                  {row.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              )}
            />
          </DataTable>
        </div>
      </div>

      {/* Alt Kısım: İadeler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-10">
        {/* Sol: Tedarikçiye İadeler */}
        <div className="bg-white shadow-lg rounded-2xl overflow-hidden border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-100 bg-orange-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-orange-600 w-1.5 h-5 rounded-full"></div>
              <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Tedarikçiye İadeler</h2>
            </div>
            <Tag value={`${product.purchasesReturns?.length || 0} İŞLEM`} className="bg-orange-600 text-[10px] font-black" />
          </div>
          <DataTable
            value={product.purchasesReturns}
            rows={5}
            paginator
            responsiveLayout="scroll"
            className="p-datatable-sm"
            emptyMessage="İade kaydı bulunamadı."
          >
            <Column field="supplierName" header="TEDARİKÇİ" sortable className="font-bold text-xs" />
            <Column
              field="quantity"
              header="MİKTAR"
              sortable
              body={(row) => (
                <span className="font-black text-slate-700">
                  {row.quantity} {row.unit}
                </span>
              )}
            />
            <Column
              field="unitPrice"
              header="ALIŞ FİYATI"
              sortable
              body={(row) => (
                <span className="font-black text-orange-600">
                  {row.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              )}
            />
            <Column
              field="returnDate"
              header="TARİH"
              sortable
              body={(row) => (
                <span className="text-slate-500 font-medium text-[11px]">
                  {new Date(row.returnDate).toLocaleDateString('tr-TR')}
                </span>
              )}
            />
          </DataTable>
        </div>

        {/* Sağ: Müşteriden İadeler */}
        <div className="bg-white shadow-lg rounded-2xl overflow-hidden border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-100 bg-red-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-red-600 w-1.5 h-5 rounded-full"></div>
              <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest m-0">Müşteriden İadeler</h2>
            </div>
            <Tag value={`${product.salesReturns?.length || 0} İŞLEM`} className="bg-red-600 text-[10px] font-black" />
          </div>
          <DataTable
            value={product.salesReturns}
            rows={5}
            paginator
            responsiveLayout="scroll"
            className="p-datatable-sm"
            emptyMessage="İade kaydı bulunamadı."
          >
            <Column
              field="returnDate"
              header="TARİH"
              sortable
              body={(row) => (
                <span className="text-slate-500 font-medium text-[11px]">
                  {new Date(row.returnDate).toLocaleDateString('tr-TR')}
                </span>
              )}
            />
            <Column field="customerName" header="MÜŞTERİ" sortable className="font-bold text-xs" />
            <Column
              field="quantity"
              header="ADET"
              sortable
              body={(row) => (
                <span className="font-black text-slate-700">{row.quantity}</span>
              )}
            />
            <Column
              field="totalAmount"
              header="TOPLAM"
              sortable
              body={(row) => (
                <span className="font-black text-red-600">
                  {row.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </span>
              )}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
}
